import { useState, useEffect } from 'react';
import { api } from '../api';

// Inspiration — "Where can I go with my points?" Discovery surface
// that takes the user's balances + travel month + home airports and
// asks Pilot to suggest 5 destinations with award sweet spots, best
// program per destination, estimated miles + cash, and one-line "why
// this is a steal" hook per pick.
export default function Inspiration({ trip }) {
  const [balances, setBalances] = useState({ amex_mr: 0, chase_ur: 0, capital_one: 0, citi_ty: 0, bilt: 0 });
  const [originAirports, setOriginAirports] = useState(['JFK']);
  const [airportInput, setAirportInput] = useState('');
  const [travelMonth, setTravelMonth] = useState('Sept 2026');
  const [cabin, setCabin] = useState('business');
  const [vibe, setVibe] = useState('');
  const [bonusDriven, setBonusDriven] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pre-fill from trip
  useEffect(() => {
    if (trip?.points_balances) {
      const next = {};
      trip.points_balances.forEach(b => { next[b.program] = Number(b.balance) || 0; });
      setBalances(b => ({ ...b, ...next }));
    }
    if (trip?.origin_airports?.length > 0) setOriginAirports(trip.origin_airports);
  }, [trip]);

  const run = async () => {
    setLoading(true); setError('');
    try {
      const res = await api.getInspiration({ balances, originAirports, travelMonth, cabin, vibe, bonusDriven });
      setResult(res.result);
    } catch (err) {
      setError(err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-4 pb-20">
      <div className="text-center my-6">
        <div className="text-brand-ink/55 text-xs font-bold uppercase tracking-[0.3em]">INSPIRATION</div>
        <h1 className="text-2xl sm:text-3xl font-black mt-1 text-brand-ink">Where can I go?</h1>
        <p className="text-sm text-brand-ink/70 mt-1 max-w-lg mx-auto">
          Tell Pilot your points + when you'd travel. We'll suggest 5 destinations your balance unlocks
          with award sweet spots + why each is a steal.
        </p>
      </div>

      <div className="bg-navy-light border border-brand-ink/15 rounded-2xl p-3.5 mb-3 space-y-3">
        {/* Balances */}
        <div>
          <div className="text-xs font-bold text-brand-ink/85 mb-1.5">Points balances</div>
          <div className="grid grid-cols-2 gap-1.5">
            {[
              ['amex_mr', 'Amex MR'], ['chase_ur', 'Chase UR'],
              ['capital_one', 'Capital One'], ['citi_ty', 'Citi TY'], ['bilt', 'Bilt'],
            ].map(([k, label]) => (
              <div key={k} className="flex items-center gap-1.5 bg-brand-ink/5 border border-brand-ink/15 rounded-lg p-2">
                <span className="text-[10px] font-bold text-brand-ink/75 shrink-0">{label}</span>
                <input type="number" min={0} step={1000}
                  value={balances[k] || 0}
                  onChange={(e) => setBalances({ ...balances, [k]: Number(e.target.value) || 0 })}
                  className="flex-1 bg-transparent text-brand-ink text-sm font-bold focus:outline-none min-w-0" />
              </div>
            ))}
          </div>
        </div>

        {/* Home airports */}
        <div>
          <div className="text-xs font-bold text-brand-ink/85 mb-1.5">Home airports</div>
          <div className="flex flex-wrap gap-1.5">
            {originAirports.map((c, i) => (
              <div key={i} className="bg-teal/20 border border-teal/40 text-teal text-xs font-bold rounded px-2 py-1 flex items-center gap-1">
                <span>✓ {c}</span>
                <button onClick={() => setOriginAirports(originAirports.filter((_, idx) => idx !== i))} className="hover:text-rose-300">✕</button>
              </div>
            ))}
            <input type="text" placeholder="Add (3 letters)" maxLength={3} value={airportInput}
              onChange={(e) => setAirportInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && airportInput.length === 3) {
                  if (!originAirports.includes(airportInput)) setOriginAirports([...originAirports, airportInput]);
                  setAirportInput('');
                }
              }}
              className="bg-brand-ink/5 border border-brand-ink/15 rounded px-2 py-1 text-xs text-brand-ink w-28" />
          </div>
        </div>

        {/* Travel month + cabin + vibe */}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] font-bold text-brand-ink/65 uppercase mb-1">Travel window</div>
            <input type="text" value={travelMonth} onChange={(e) => setTravelMonth(e.target.value)}
              placeholder="e.g. Sept 2026 or flexible"
              className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg px-2 py-2 text-xs text-brand-ink" />
          </div>
          <div>
            <div className="text-[10px] font-bold text-brand-ink/65 uppercase mb-1">Cabin</div>
            <select value={cabin} onChange={(e) => setCabin(e.target.value)}
              className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg px-2 py-2 text-xs text-brand-ink">
              <option value="economy">Economy</option>
              <option value="premium">Premium econ</option>
              <option value="business">Business</option>
              <option value="first">First</option>
            </select>
          </div>
        </div>

        <div>
          <div className="text-[10px] font-bold text-brand-ink/65 uppercase mb-1">Vibe (optional)</div>
          <input type="text" value={vibe} onChange={(e) => setVibe(e.target.value)}
            placeholder="e.g. beach + foodie, snowy mountains, off-the-beaten-path culture"
            className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg px-2 py-2 text-xs text-brand-ink" />
        </div>

        {/* Bonus-driven toggle — bias Pilot's picks toward routes that
            CURRENT transfer bonuses unlock cheapest. Critical for points
            travelers chasing the best monthly deals. */}
        <div className="bg-gradient-to-r from-amber-500/10 to-rose-500/5 border-2 border-amber-500/40 rounded-lg p-2.5 flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-amber-300">🔥 Bonus-driven mode</div>
            <div className="text-[10px] text-brand-ink/70 mt-0.5">
              Pilot biases picks toward destinations where ACTIVE transfer bonuses (Amex MR → Flying Blue +25%, Bilt → Aeroplan, etc.) unlock outsized {cabin} value RIGHT NOW.
            </div>
          </div>
          <button
            onClick={() => setBonusDriven(!bonusDriven)}
            className={`relative w-11 h-6 rounded-full transition shrink-0 ${bonusDriven ? 'bg-amber-400' : 'bg-brand-ink/20'}`}
          >
            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition ${bonusDriven ? 'left-[22px]' : 'left-0.5'}`} />
          </button>
        </div>

        <button onClick={run} disabled={loading} className="w-full bg-gradient-to-r from-teal to-gold text-navy font-bold py-3 rounded-xl disabled:opacity-40">
          {loading ? 'Pilot is thinking…' : bonusDriven ? '🔥 Find bonus opportunities' : '✨ Suggest destinations'}
        </button>
        {error && <div className="text-xs text-rose-300">{error}</div>}
      </div>

      {result && (
        <div className="space-y-3">
          <div className="bg-gradient-to-br from-teal/10 to-gold/5 border border-teal/30 rounded-xl p-3.5">
            <div className="text-[10px] font-bold text-teal uppercase tracking-wider mb-1">Pilot's read</div>
            <p className="text-sm text-brand-ink leading-relaxed">{result.intro}</p>
          </div>
          {(result.picks || []).map((p, i) => (
            <div key={i} className="bg-navy-light border border-brand-ink/15 rounded-xl p-3">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-[10px] font-bold text-gold uppercase tracking-wider">Pick {i + 1}</div>
                  <div className="text-base font-black text-brand-ink">{p.city}, {p.country}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-brand-ink/65">via {p.best_program}</div>
                  <div className="text-sm font-bold text-teal">{(p.estimated_miles_per_person || 0).toLocaleString()} mi/pp</div>
                  {p.estimated_cash > 0 && <div className="text-[10px] text-brand-ink/60">+ ~${p.estimated_cash.toLocaleString()} cash</div>}
                </div>
              </div>
              {p.sweet_spot && (
                <div className="mt-2 bg-gold/10 border border-gold/25 rounded p-1.5 text-[11px] text-brand-ink/85">
                  <strong className="text-gold">🎯 Sweet spot:</strong> {p.sweet_spot}
                </div>
              )}
              <p className="text-[12px] text-brand-ink/85 leading-snug mt-2">{p.why}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
